/**
 * Content Carousel - Reusable 3D Perspective Carousel
 * Supports multiple carousels on the same page
 */

(function() {
  'use strict';

  class ContentCarousel {
    constructor(container, options = {}) {
      this.container = container;
      this.track = container.querySelector('.content-carousel-track');
      this.cards = container.querySelectorAll('.content-carousel-card');
      this.prevBtn = container.querySelector('.content-carousel-nav--prev');
      this.nextBtn = container.querySelector('.content-carousel-nav--next');
      this.carouselId = container.dataset.carouselId || container.closest('.content-carousel-wrapper')?.querySelector('.content-carousel-track')?.dataset.carouselId;
      
      this.currentIndex = 0;
      this.totalCards = this.cards.length;
      this.autoplay = options.autoplay || false;
      this.autoplayDelay = (options.autoplayDelay || 5) * 1000;
      this.autoplayInterval = null;
      
      if (!this.track || !this.cards.length) return;
      
      this.init();
    }

    init() {
      this.updateCarousel();
      this.attachEventListeners();
      
      if (this.autoplay) {
        this.startAutoplay();
      }
      
      // Pause autoplay on hover
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
      
      // Handle window resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.updateCarousel();
        }, 250);
      });
    }

    updateCarousel() {
      this.cards.forEach((card, index) => {
        card.classList.remove('center_card', 'side_card', 'left_card', 'right_card');
        
        if (index === this.currentIndex) {
          card.classList.add('center_card');
        } else {
          card.classList.add('side_card');
          
          // Calculate if card is on left or right (considering circular)
          const diff = index - this.currentIndex;
          const adjustedDiff = diff > this.totalCards / 2 ? diff - this.totalCards : 
                               diff < -this.totalCards / 2 ? diff + this.totalCards : diff;
          
          if (adjustedDiff < 0) {
            card.classList.add('left_card');
          } else {
            card.classList.add('right_card');
          }
        }
      });

      // Calculate translation based on actual card width
      if (this.cards[0]) {
        const cardWidth = this.cards[0].offsetWidth;
        const gap = 0; // Must match CSS gap
        const offset = this.currentIndex * (cardWidth + gap);
        
        // Center the active card in viewport
        const containerWidth = this.track.parentElement.offsetWidth;
        const centerOffset = (containerWidth / 2) - (cardWidth / 2);
        
        this.track.style.transform = `translateX(${centerOffset - offset}px)`;
      }
    }

    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.totalCards;
      this.updateCarousel();
    }

    prevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.totalCards) % this.totalCards;
      this.updateCarousel();
    }

    attachEventListeners() {
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.nextSlide());
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prevSlide());
      }

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          this.nextSlide();
        }
      });

      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      this.track.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.track.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    }

    handleSwipe(startX, endX) {
      const swipeThreshold = 50;
      if (startX - endX > swipeThreshold) {
        this.nextSlide();
      }
      if (endX - startX > swipeThreshold) {
        this.prevSlide();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }
  }

  // Initialize all carousels on page load
  function initCarousels() {
    const carouselWrappers = document.querySelectorAll('.content-carousel-wrapper');
    
    carouselWrappers.forEach((wrapper) => {
      const track = wrapper.querySelector('.content-carousel-track');
      if (!track) return;
      
      // Get settings from data attributes or section settings
      const autoplay = track.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(track.dataset.autoplayDelay) || 5;
      
      new ContentCarousel(wrapper, {
        autoplay: autoplay,
        autoplayDelay: autoplayDelay
      });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initCarousels);
  }
})();
